import { PlotComponent, getGLFormatTypeAlignment } from './PlotComponent';
import { hex2rgba } from './utils';
import { WGLProgram, WGLTexture } from 'autumn-wgl';
const contour_vertex_shader_src = `uniform mat4 u_matrix;
uniform int u_offset;

attribute vec2 a_pos;
attribute float a_grid_cell_size;
attribute vec2 a_tex_coord;

varying highp vec2 v_tex_coord;
varying highp float v_grid_cell_size;
varying highp float v_map_scale_fac;

void main() {
    float globe_width = 1.;
    vec2 globe_offset = vec2(globe_width * float(u_offset), 0.);

    gl_Position = u_matrix * vec4(a_pos + globe_offset, 0.0, 1.0);
    v_tex_coord = a_tex_coord;
    v_grid_cell_size = a_grid_cell_size;

    
    highp float lat = 2. * atan(exp(a_pos.y / 6371229.0)) - 3.1414592654 / 2.;
    v_map_scale_fac = cos(lat);
}`
const contour_fragment_shader_src = `#extension GL_OES_standard_derivatives : enable
#define MAX_N_CONTOURS 40

varying highp vec2 v_tex_coord;
varying highp float v_grid_cell_size;
varying highp float v_map_scale_fac;

uniform sampler2D u_fill_sampler;
uniform highp float u_contour_interval;
uniform highp float u_contour_levels[MAX_N_CONTOURS];
uniform int u_num_contours;
uniform lowp float u_line_cutoff;
uniform lowp vec3 u_color;
uniform lowp vec2 u_step_size;
uniform lowp float u_zoom_fac;

void main() {
    highp float field_val = texture2D(u_fill_sampler, v_tex_coord).r;

    lowp vec2 grid_point = fract(v_tex_coord / u_step_size + vec2(0.5, 0.5));
    highp vec2 grid_sw = v_tex_coord - grid_point * u_step_size;

    lowp vec2 ihat = vec2(u_step_size.x, 0.0);
    lowp vec2 jhat = vec2(0.0, u_step_size.y);
    highp float fv_sw = texture2D(u_fill_sampler, grid_sw).r;
    highp float fv_se = texture2D(u_fill_sampler, grid_sw + ihat).r;
    highp float fv_nw = texture2D(u_fill_sampler, grid_sw + jhat).r;
    highp float fv_ne = texture2D(u_fill_sampler, grid_sw + ihat + jhat).r;

    highp float dfdx = mix(fv_se, fv_ne, grid_point.y) - mix(fv_sw, fv_nw, grid_point.y);
    highp float dfdy = mix(fv_nw, fv_ne, grid_point.x) - mix(fv_sw, fv_se, grid_point.x);
    highp float fwidth_field = sqrt((dfdx * dfdx + dfdy * dfdy) / (5e5 * v_grid_cell_size));

    lowp float plot_val;

    if (u_num_contours > 0) {
        highp float min_contour_diff = u_contour_levels[1] - u_contour_levels[0];
        bool assigned_contour = false;
        highp float low_contour;
        highp float high_contour;
        highp float highest_contour;

        for (int ncnt = 0; ncnt < MAX_N_CONTOURS; ncnt++) {
            if (ncnt >= u_num_contours) { break; }

            min_contour_diff = min(min_contour_diff, u_contour_levels[ncnt + 1] - u_contour_levels[ncnt]);

            if (u_contour_levels[ncnt] < field_val && field_val < u_contour_levels[ncnt + 1]) {
                assigned_contour = true;

                low_contour = u_contour_levels[ncnt];
                high_contour = u_contour_levels[ncnt + 1];
            }

            highest_contour = u_contour_levels[ncnt];
        }

        if (!assigned_contour) {
            if (field_val < u_contour_levels[0]) {
                plot_val = (u_contour_levels[0] - field_val) / min_contour_diff;
            }
            else if (field_val > highest_contour) {
                plot_val = (field_val - highest_contour) / min_contour_diff;
            }
        }
        else {
            plot_val = min(field_val - low_contour, high_contour - field_val) / min_contour_diff;
        }
    }
    else {
        plot_val = fract(field_val / u_contour_interval);
        if (plot_val > 0.5) plot_val = 1.0 - plot_val;
    }

    plot_val = plot_val / (max(0.001, fwidth_field / (u_zoom_fac * 0.125)));

    if (plot_val > u_line_cutoff) discard;

    gl_FragColor = vec4(u_color, 1. - (plot_val * plot_val / (u_line_cutoff * u_line_cutoff)));
}`
/**
 * A field of contoured data. The contours can optionally be thinned based on map zoom level.
 * @example
 * // Create a contoured height field, with black contours every 30 m (assuming the height field is in
 * // meters) and only using every other contour when the map zoom level is less than 5.
 * const contours = new Contour(height_field, {color: '#000000', interval: 30,
 *                                                  thinner: zoom => zoom < 5 ? 2 : 1});
 */
class Contour extends PlotComponent {
    /**
     * Create a contoured field
     * @param field - The field to contour
     * @param opts  - Options for creating the contours
     */
    constructor(field, opts) {
        super();
        this.field = field;
        this.interval = opts.interval || 1;
        this.levels = opts.levels || [];
        const color = hex2rgba(opts.color || '#000000');
        this.color = [color[0], color[1], color[2]];
        this.thinner = opts.thinner || (() => 1);
        this.gl_elems = null;
    }
    /**
     * @internal
     * Add the contours to a map
     */
    async onAdd(map, gl) {
        // Basic procedure for these contours from https://www.shadertoy.com/view/lltBWM
        gl.getExtension("OES_standard_derivatives");
        const program = new WGLProgram(gl, contour_vertex_shader_src, contour_fragment_shader_src);
        const { vertices: verts_buf, texcoords: tex_coords_buf, cellsize: cellsize_buf } = await this.field.grid.getWGLBuffers(gl);
        const vertices = verts_buf;
        const texcoords = tex_coords_buf;
        const grid_cell_size = cellsize_buf;
        const { format, type, row_alignment } = getGLFormatTypeAlignment(gl, this.field.isFloat16());
        const fill_image = { 'format': format, 'type': type,
            'width': this.field.grid.ni, 'height': this.field.grid.nj, 'image': this.field.getTextureData(),
            'mag_filter': gl.LINEAR, 'row_alignment': row_alignment,
        };
        const fill_texture = new WGLTexture(gl, fill_image);
        this.gl_elems = {
            map: map, program: program, vertices: vertices, texcoords: texcoords, grid_cell_size: grid_cell_size, fill_texture: fill_texture
        };
    }
    /**
     * @internal
     * Render the contours
     */
    render(gl, matrix) {
        if (this.gl_elems === null)
            return;
        const gl_elems = this.gl_elems;
        if (matrix instanceof Float32Array)
            matrix = [...matrix];
        const zoom = gl_elems.map.getZoom();
        const intv = this.thinner(zoom) * this.interval;
        const cutoff = 0.3 / intv;
        const step_size = [1 / this.field.grid.ni, 1 / this.field.grid.nj];
        const zoom_fac = Math.pow(2, zoom);
        let uniforms = { 'u_contour_interval': intv, 'u_line_cutoff': cutoff, 'u_color': this.color, 'u_step_size': step_size, 'u_zoom_fac': zoom_fac,
            'u_matrix': matrix, 'u_num_contours': 0, 'u_contour_levels': [0], 'u_offset': 0 };
        if (this.levels.length > 0) {
            uniforms = { ...uniforms, 'u_num_contours': this.levels.length, 'u_contour_levels': this.levels };
        }
        gl_elems.program.use({ 'a_pos': gl_elems.vertices, 'a_grid_cell_size': gl_elems.grid_cell_size, 'a_tex_coord': gl_elems.texcoords }, uniforms, { 'u_fill_sampler': gl_elems.fill_texture });
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl_elems.program.draw();
        gl_elems.program.setUniforms({ 'u_offset': -2 });
        gl_elems.program.draw();
        gl_elems.program.setUniforms({ 'u_offset': -1 });
        gl_elems.program.draw();
        gl_elems.program.setUniforms({ 'u_offset': 1 });
        gl_elems.program.draw();
    }
}
export default Contour;
